import { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { Size } from 'govwell-ui/types'
import { RecordTaskOwnerOptionFragment } from 'types/graphql'

import { useRecordTaskReassignmentInfoQuery } from 'src/fetch/recordTasks'

type Props = {
  autoFocus?: boolean
  caption?: React.ReactNode
  errorMessage?: React.ReactNode
  isDisabled?: boolean
  isInvalid?: boolean
  isRequired?: boolean
  label?: React.ReactNode
  onBlur?: () => void
  onValueChange: (value: RecordTaskOwnerOptionFragment | undefined) => void
  recordTaskId: number
  size?: Size
  value: RecordTaskOwnerOptionFragment | null | undefined
  width?: React.CSSProperties['width']
}
const RecordTaskOwnerSelect = ({
  autoFocus,
  caption,
  errorMessage,
  isDisabled,
  isInvalid,
  isRequired,
  label,
  onBlur,
  onValueChange,
  recordTaskId,
  size,
  value,
  width,
}: Props) => {
  const { data, isLoading } = useRecordTaskReassignmentInfoQuery(recordTaskId)
  const possibleRecordTaskOwners = useMemo(
    () => data?.recordTaskOwnerOptions ?? [],
    [data?.recordTaskOwnerOptions]
  )
  const options: SelectOption<RecordTaskOwnerOptionFragment>[] = useMemo(
    () =>
      possibleRecordTaskOwners.map((prto) => ({
        label: prto.label,
        value: prto,
      })),
    [possibleRecordTaskOwners]
  )

  const selectedOption = useMemo(
    () => options.find((o) => o.value.user.id === value?.user.id),
    [options, value?.user.id]
  )
  const handleSelectedOptionChange = useCallback(
    (newSelectedOption: SelectOption<RecordTaskOwnerOptionFragment> | undefined) => {
      onValueChange(newSelectedOption?.value)
    },
    [onValueChange]
  )

  return (
    <Select<RecordTaskOwnerOptionFragment>
      autoFocus={autoFocus}
      caption={caption}
      errorMessage={errorMessage}
      getIsOptionDisabled={(o) => o.value.isDisabled}
      getOptionKey={(o) => o.value.user.id}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isLoading={isLoading}
      isRequired={isRequired}
      label={label}
      onBlur={onBlur}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder="Select new assignee"
      selectedOption={selectedOption}
      size={size}
      width={width}
    />
  )
}

export default React.memo(RecordTaskOwnerSelect)
